import { appConfig, auth, callable, db } from "./db.js";

let creatingFlightTestCrewAccount = false;

// =============================================================================
// Auth State
// -----------------------------------------------------------------------------
// Centralises all authentication-related side effects that previously lived in
// App.vue: reacting to Firebase auth state changes, loading the correct user
// record (internal vs. pure-external claim users), redirecting externals to
// their self views, account creation and sign out.
//
// Runtime singletons (store / router / toast) and the login box ref are passed
// in via initAuthState(); the Firebase primitives (db / auth / callable) are
// imported directly from ./db.js.
// =============================================================================

// -----------------------------------------------------------------------------
// Admin Fake Claims Override (Issue #183)
// -----------------------------------------------------------------------------
// Allows admins to override effective tenant custom claims CLIENT-ONLY for 
// smoke-testing without writing Auth server claims. Session-local; clearing 
// restores real token claims. Never calls Auth Admin APIs.
//
// Storage key for fake claim overrides in sessionStorage
const FAKE_CLAIMS_KEY = "stc_admin_fake_claims";

/**
 * Get admin fake claim overrides from sessionStorage (if any).
 * Returns null if no overrides are set.
 * 
 * @returns {Object|null} Fake claim object or null
 */
export function getAdminFakeClaimOverride() {
  try {
    const stored = sessionStorage.getItem(FAKE_CLAIMS_KEY);
    if (!stored) return null;
    return JSON.parse(stored);
  } catch (e) {
    console.error("Failed to parse admin fake claims:", e);
    return null;
  }
}

/**
 * Set admin fake claim overrides (client-only, session-local).
 * Pass null to clear overrides.
 * 
 * @param {Object|null} fakeClaimOverride - Fake claim object with flight_test_crew/signatory/etc keys
 */
export function setAdminFakeClaimOverride(fakeClaimOverride) {
  try {
    if (fakeClaimOverride === null) {
      sessionStorage.removeItem(FAKE_CLAIMS_KEY);
    } else {
      sessionStorage.setItem(FAKE_CLAIMS_KEY, JSON.stringify(fakeClaimOverride));
    }
  } catch (e) {
    console.error("Failed to set admin fake claims:", e);
  }
}

// -----------------------------------------------------------------------------
// Tenant Claims Helper
// -----------------------------------------------------------------------------
/**
 * Check if the user has access to the current tenant based on nested claims.
 * 
 * Migration window: if new nested structure is missing, fall back to old flat claims
 * for backward compatibility. Once the new structure is present, the flat fallback
 * is ignored.
 * 
 * Admin fake claims override: if admin has set fake claims (client-only), those
 * are merged with real claims. The merge ALWAYS preserves `internal: true` from
 * real claims to prevent signing out the admin and losing the Clear UI.
 * 
 * @param {Object} claims - Custom claims from ID token
 * @param {string} tenantKey - Tenant key (stc, doa, test)
 * @param {boolean} [allowFakeOverride=true] - Whether to check for admin fake claim overrides
 * @returns {Object} Tenant claim object or null if no access
 */
function getTenantClaim(claims, tenantKey, allowFakeOverride = true) {
    // First, get the real tenant claim from token
    let realTenantClaim = null;
    
    // Check if ANY nested tenant structure exists (stc, doa, or test)
    const hasNestedStructure = !!(claims.stc || claims.doa || claims.test);
    
    if (hasNestedStructure) {
        // New structure present: use it exclusively
        realTenantClaim = claims[tenantKey] || null;
    } else {
        // Migration window: no nested structure yet, accept old flat claims
        // Map old flat keys to a synthesized tenant claim for this tenant only
        const hasFlatInternal = claims.internal === true;
        const hasFlatFlightTestCrew = claims.flight_test_crew === true;
        const hasFlatSignatory = claims.signatory === true;
        const hasFlatSignatoryMilitary = claims.signatory_military === true;
        const hasFlatCustomer = claims.customer === true;
        const flatCustomerId = claims.customer_id;
        
        // During migration window, accept any flat key as granting access to this tenant
        if (hasFlatInternal || hasFlatFlightTestCrew || hasFlatSignatory || hasFlatSignatoryMilitary || hasFlatCustomer) {
            realTenantClaim = {
                internal: hasFlatInternal || undefined,
                flight_test_crew: hasFlatFlightTestCrew || undefined,
                signatory: hasFlatSignatory || undefined,
                signatory_military: hasFlatSignatoryMilitary || undefined,
                customer: hasFlatCustomer || undefined,
                customer_id: flatCustomerId || undefined,
            };
        }
    }
    
    // If no real tenant claim, user has no access
    if (!realTenantClaim) return null;
    
    // Check for admin fake claim override (if enabled)
    if (allowFakeOverride) {
        const fakeOverride = getAdminFakeClaimOverride();
        if (fakeOverride) {
            // Merge fake flags with real claims, ALWAYS preserving internal: true
            // so admin stays signed in and AccountPopup gate continues to work
            return {
                ...realTenantClaim,
                ...fakeOverride,
                // Force internal to remain true if it was true in real claims
                // This prevents the admin from losing session / Clear UI
                // internal: realTenantClaim.internal === true ? true : fakeOverride.internal
            };
        }
    }
    
    return realTenantClaim;
}

// -----------------------------------------------------------------------------
// External (claim-based) user loading
// -----------------------------------------------------------------------------
// -----------------------------------------------------------------------------
// External (claim-based) user loading
// -----------------------------------------------------------------------------
// Pure external self-service users (flight-test-crew / signatory claims) watch
// their canonical direct-UID record into "user" and skip the heavy internal
// loads. Claims are the source of truth for the user type.
//
// Migration window: getTenantClaim() handles both nested and flat claim structures.
//
// Signatory variant resolution:
// - claim 'signatory' -> STC tree: Matrices/SignatoryMatrix/Signatories/{uid}
// - claim 'signatory_military' -> Military tree: Matrices/MilitarySignatoryMatrix/Signatories/{uid}
async function loadExternalUser(user, claims, store) {
    const tenantKey = appConfig.appPrefix;
    const tenantClaim = getTenantClaim(claims, tenantKey);
    
    if (!tenantClaim) {
      throw new Error(`Your account does not have access to the ${tenantKey.toUpperCase()} application.`);
    }
    
    const isFlightTestCrew = !!tenantClaim.flight_test_crew;
    const isSignatory = !!tenantClaim.signatory;
    const isSignatoryMilitary = !!tenantClaim.signatory_military;
    const isCustomer = !!tenantClaim.customer;
    
    
  if (isFlightTestCrew) {
    const externalUser = await db.watch(`FlightTestCrew/${user.uid}`, store, "user");
    if (!externalUser) throw new Error("Your flight test crew profile could not be loaded.");
  } else if (isSignatoryMilitary) {
    // Military signatory: watch the military tree
    const externalUser = await db.watch(`Matrices/MilitarySignatoryMatrix/Signatories/${user.uid}`, store, "user");
    if (!externalUser) throw new Error("Your military signatory profile could not be loaded.");
  } else if (isSignatory) {
    // STC signatory: watch the STC tree
    const externalUser = await db.watch(`Matrices/SignatoryMatrix/Signatories/${user.uid}`, store, "user");
    if (!externalUser) throw new Error("Your signatory profile could not be loaded.");
  } else if (isCustomer) {
    const customerUser = await db.watch(`CustomersUserdata/${user.uid}`, store, "user");
    const customerId = tenantClaim.customer_id;
    if (customerUser?.Enabled !== true || customerId !== customerUser.CustomerID) {
      throw new Error("Your customer portal profile is disabled or does not match your account.");
    }
    await db.watch(`CustomerPortal/${customerId}/stc/DesignChanges`, store, "CustomerPortal/stc/DesignChanges");
    if (Object.values(customerUser.Permissions?.military || {}).some(Boolean)) {
      await db.watch(`CustomerPortal/${customerId}/military/DesignChanges`, store, "CustomerPortal/military/DesignChanges");
    }
  }

  localStorage.setItem("expectUserSignIn", 1);
}

// -----------------------------------------------------------------------------
// Internal / mixed user loading
// -----------------------------------------------------------------------------
// Traditional internal users load their Userdata record plus the supporting
// data sets (leave events, users list, etc.) gated by their security flags.
async function loadInternalUser(user, store) {
  const userd = await db.watch(`Userdata/${user.uid}`, store, "user");

  if (!userd) {
    throw new Error("User not found. You may not have access to this application.");
  }

  if (userd?.Security?.Audits?.Read === true) {
    // const file = await db.once("Audits/Settings/Handbook", store, "handbook");
    // // const handbookURL = await db.download(file);
    // store.commit("save", {
    //   handbook: handbookURL,
    // });
  }

  if (userd?.Security?.Leave?.Read === true) {
    // get and store the leave objects
    const today = new Date();
    const currentTime = today.getTime();
    const upcomingAndRecentLeaveEvents = await db
      .ref("Leave/Events")
      .orderByChild("Date/Time")
      .startAfter(currentTime - 1000 * 60 * 60 * 24 * 21)
      .endBefore(currentTime + 1000 * 60 * 60 * 24 * 21)
      .once("value");

    if (upcomingAndRecentLeaveEvents.val() === null) return;

    const events = Object.values(upcomingAndRecentLeaveEvents.val() || {}).reduce((acc, i) => {
      const ids = Object.keys(i.Users || {});
      for (const id of ids) {
        if (!acc[id]) {
          acc[id] = [];
        }
        acc[id].push(i);
      }
      return acc;
    }, {});

    store.commit("save", {
      upcomingAndRecentLeaveEvents: events,
    });
  }

  // get all the users and store them
  db.store("users", store);
  db.store("business/leave/events", store);

  // set localstore handler
  localStorage.setItem("expectUserSignIn", 1);
}

// -----------------------------------------------------------------------------
// Auth state change handler
// -----------------------------------------------------------------------------
export async function failAuthentication({
  store,
  toast,
  loginBox,
  message,
  severity = "error",
  signOut = false,
}) {
  store.commit("save", {
    user: null,
    notifications: null,
    impersonation: null,
    failedUser: true,
  });
  store.commit("setTenantClaim", null);
  localStorage.removeItem("expectSignIn");
  localStorage.removeItem("expectUserSignIn");
  loginBox?.value?.reset?.();

  if (message) {
    if (severity === "warning") {
      toast.warning(message);
    } else {
      toast.error(message);
    }
  }

  if (signOut && auth.currentUser) {
    try {
      await auth.signOut();
    } catch (error) {
      console.error("Failed to sign out unauthorized user:", error);
    }
  }
}

// Routes each auth state change to the right loader. Returns a handler bound to
// the provided store / toast so it can be passed to auth.onAuthStateChanged.
function createAuthStateHandler({ store, toast, router, loginBox }) {
  let watchedNotificationsPath = null;
  let watchedUserPath = null;

  // if the user is creating an account navigate them to thte base route so they do not get redirected to the login page
  return async function manageUserAuthenticationState(user) {
    // If no user data is passed then clear the store auth state and user object.
    if (!user) {
      if (watchedUserPath) {
        db.unwatch(watchedUserPath, "user");
        watchedUserPath = null;
      }
      if (watchedNotificationsPath) {
        db.unwatch(watchedNotificationsPath, "notifications");
        watchedNotificationsPath = null;
      }
      await failAuthentication({ store, toast, loginBox });
      return;
    }
    
    // Determine if this is a pure external self-service user (claim present) vs
    // internal. Claims are the source of truth for type.
    let idTokenResult = null;
    try {
      idTokenResult = await user.getIdTokenResult();
    } catch (error) {
      console.error("Failed to verify user access:", error);
      await failAuthentication({
        store,
        toast,
        loginBox,
        message: "We could not verify your application access. Please log in again.",
        signOut: true,
      });
      return;
    }
    
    const claims = idTokenResult?.claims || {};
    store.commit("save", {
      impersonation: claims.impersonation || null,
    });
    
    const tenantKey = appConfig.appPrefix;
    const tenantClaim = getTenantClaim(claims, tenantKey);
    const realTenantClaim = getTenantClaim(claims, tenantKey, false);
    
    // Store tenant claim for synchronous access (app switcher, etc.)
    store.commit("setTenantClaim", tenantClaim);
    
    // Check if user has access to THIS tenant
    if (!tenantClaim) {
      if (creatingFlightTestCrewAccount) return;

      await failAuthentication({
        store,
        toast,
        loginBox,
        message: `Your account does not have access to the ${tenantKey.toUpperCase()} application.`,
        signOut: true,
      });
      return;
    }
    
    const isFlightTestCrew = !!realTenantClaim.flight_test_crew;
    const isSignatory = !!realTenantClaim.signatory;
    const isSignatoryMilitary = !!realTenantClaim.signatory_military;
    const isCustomer = !!realTenantClaim.customer;
    const isInternal = !!realTenantClaim.internal;
    const isPureExternal = (isFlightTestCrew || isSignatory || isSignatoryMilitary || isCustomer) && !isInternal;

    const nextUserPath = isFlightTestCrew
      ? `FlightTestCrew/${user.uid}`
      : isSignatoryMilitary
        ? `Matrices/MilitarySignatoryMatrix/Signatories/${user.uid}`
        : isSignatory
          ? `Matrices/SignatoryMatrix/Signatories/${user.uid}`
          : isCustomer
            ? `CustomersUserdata/${user.uid}`
            : `Userdata/${user.uid}`;

    if (watchedUserPath && watchedUserPath !== nextUserPath) {
      db.unwatch(watchedUserPath, "user");
    }
    watchedUserPath = nextUserPath;

    try {
      if (isPureExternal) {
        router.push("/");
        await loadExternalUser(user, claims, store);
        store.commit("save", { failedUser: false });
        maybeRedirectExternalToSelf(user, claims, { store, router });
        toast.success("Signed in successfully!");
        return;
      }

      // Internal users receive the shared application watchers after claim routing.
      await db.watch("counts", store);
      const notificationsPath = `Notifications/${user.uid}`;
      if (watchedNotificationsPath && watchedNotificationsPath !== notificationsPath) {
        db.unwatch(watchedNotificationsPath, "notifications");
      }
      watchedNotificationsPath = notificationsPath;
      await db.watch(notificationsPath, store, "notifications");

      await loadInternalUser(user, store);
      store.commit("save", { failedUser: false });
    } catch (error) {
      console.error("Failed to load authenticated user:", error);
      if (watchedUserPath) {
        db.unwatch(watchedUserPath, "user");
        watchedUserPath = null;
      }
      if (watchedNotificationsPath) {
        db.unwatch(watchedNotificationsPath, "notifications");
        watchedNotificationsPath = null;
      }
      await failAuthentication({
        store,
        toast,
        loginBox,
        message: error?.message || "Your account could not be loaded. Please try again.",
        signOut: true,
      });
    }
  };
}

// -----------------------------------------------------------------------------
// External self-view redirect
// -----------------------------------------------------------------------------
// Runs immediately after an external user's record is loaded. If they land on
// a non-self internal route, push them to a sensible self landing.
//
// Migration window: getTenantClaim() is already used above to determine claim type.
//
// Signatory variant routing:
// - signatory claim -> /Matrices/SignatoryMatrix/Self (STC)
// - signatory_military claim -> /Matrices/MilitarySignatoryMatrix/Self (Military)
//
// Dual-claim handling:
// - When a user has both FTC and Signatory claims, prefer FTC as the default landing
// - Do NOT re-push on every auth tick; only redirect on initial landing or invalid routes
// - Allow switching between Self apps and Overview without forced redirects
function maybeRedirectExternalToSelf(user, claims, { store, router }) {
  const tenantKey = appConfig.appPrefix;
  const tenantClaim = getTenantClaim(claims, tenantKey);
  
  if (!tenantClaim) return;

  const path = router.currentRoute.value?.path || "";
  
  // Allow Overview (/) - don't force away from it
  if (path === "/") return;
  
  // Allow staying on valid Self routes the user owns
  // FlightTestCrew Self, Signatory Self (both STC and Military)
  const isFlightTestCrewSelf = path.includes("/FlightTestCrew/Self");
  const isSignatorySelf = path.includes("/Matrices/SignatoryMatrix/Self") || path.includes("/Matrices/MilitarySignatoryMatrix/Self");
  const isCustomerPortal = path.includes("/CustomerPortal");
  
  // If already on a valid Self or Overview, don't redirect
  if (isFlightTestCrewSelf || isSignatorySelf || isCustomerPortal) return;
  
  // Only redirect if landing on an invalid/internal route
  // Determine the default Self landing based on claims (prefer FTC for dual-claim users)
  if(tenantClaim.flight_test_crew) {
    router.push("/FlightTestCrew/Self");
  } else if(tenantClaim.signatory_military) {
    router.push("/Matrices/MilitarySignatoryMatrix/Self");
  } else if(tenantClaim.signatory) {
    router.push("/Matrices/SignatoryMatrix/Self");
  } else if (tenantClaim.customer) {
    router.push("/CustomerPortal/stc/DesignChanges");
  } else { 
    // CREATE 401 / 403 page for unknown claim types? For now just log and don't redirect.
    console.warn("Unknown external claim type for user", user.uid, claims);
  }
}

// -----------------------------------------------------------------------------
// Account creation (FTC self-signup)
// -----------------------------------------------------------------------------
// Ported/adapted from the standalone flight-test-crew app for FTC self-account
// creation inside the main app.
function createOnCreateAccountTry({ toast, loginBox, store, router }) {
  return async function onCreateAccountTry({ firstName, lastName, email, password }) {
    creatingFlightTestCrewAccount = true;

    try {
      const userCredential = await auth.createUserWithEmailAndPassword(email, password);
      await userCredential.user.updateProfile({
        displayName: `${firstName} ${lastName}`,
      });

      // Initialise the canonical FlightTestCrew/{uid} record server-side (also sets the claim).
      const createFlightTestCrewUser = callable("createFlightTestCrewUser");
      await createFlightTestCrewUser({
        firstName,
        lastName,
        email,
        databaseURL: appConfig?.firebaseConfig?.databaseURL
      });

      const user = auth.currentUser;
      await user.getIdToken(true);
      const claims = (await user.getIdTokenResult()).claims;

      const tenantClaim = getTenantClaim(claims, appConfig.appPrefix);
      if (!tenantClaim?.flight_test_crew) {
        throw new Error("Your Flight Test Crew access could not be verified. Please log in again.");
      }

      store.commit("setTenantClaim", tenantClaim);
      await loadExternalUser(user, claims, store);
      store.commit("save", { failedUser: false });
      maybeRedirectExternalToSelf(user, claims, { store, router });
      toast.success("Account created successfully!");

      // The refreshed claim now allows guards to enter the clean self view.
    } catch (err) {
      console.error("ERROR", err);        
      loginBox.value?.reset?.();
      toast.error(err.message || "Failed to create account");
    } finally {
      creatingFlightTestCrewAccount = false;
    }
  };
}

// -----------------------------------------------------------------------------
// Sign out
// -----------------------------------------------------------------------------
export function signout(store) {
  auth.signOut();
  store.commit("signout");
  localStorage.removeItem("expectSignIn");
  localStorage.removeItem("expectUserSignIn");
}


// -----------------------------------------------------------------------------
// Public init
// -----------------------------------------------------------------------------
// Wires up the auth state listener and the external redirect, and returns the
// handlers the component needs in its template (onCreateAccountTry / signout).
export function initAuthState({ store, router, toast, loginBox }) {
  // watch the auth state from auth / db
  auth.onAuthStateChanged(createAuthStateHandler({ store, toast, router, loginBox }));

  return {
    onCreateAccountTry: createOnCreateAccountTry({ toast, loginBox, store, router }),
    signout,
  };
}

/**
 * Export getTenantClaim, getAdminFakeClaimOverride, and setAdminFakeClaimOverride
 * for use in route guards, UI components, and other client code.
 */
export { getTenantClaim, getAdminFakeClaimOverride as getAdminFakeClaims, setAdminFakeClaimOverride as setAdminFakeClaims };
